import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api/client";
import type { components } from "@dolfin/api-admin";
import { Badge } from "@/components/ui/badge";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PaginationBar } from "@/components/PaginationBar";
import { useCursorPagination } from "@/hooks/useCursorPagination";
import { formatCurrency, formatDateTime } from "@/lib/format";

type CreditNote = components["schemas"]["CreditNoteResponse"];

interface OrganisationCreditNotesTableProps {
  organisationId?: string;
}

function statusVariant(
  status: string | undefined,
): "default" | "secondary" | "outline" | "destructive" {
  switch (status) {
    case "Issued":
      return "default";
    case "Draft":
      return "secondary";
    default:
      return "outline";
  }
}

export function OrganisationCreditNotesTable({
  organisationId,
}: OrganisationCreditNotesTableProps) {
  const pagination = useCursorPagination({ initialLimit: 20 });

  const [creditNotes, setCreditNotes] = useState<CreditNote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function fetchCreditNotes() {
      setLoading(true);
      setError(null);
      const { data, error: apiError } = await api.GET("/admin/credit-notes", {
        params: {
          query: {
            OrganisationId: organisationId,
            Cursor: pagination.cursor,
            Limit: pagination.limit,
          },
        },
      });
      if (cancelled) return;
      if (apiError) {
        setError("Failed to load credit notes.");
      } else {
        setCreditNotes(data?.data ?? []);
        pagination.setPage({
          nextCursor: data?.meta?.cursors?.next,
          previousCursor: data?.meta?.cursors?.previous,
        });
      }
      setLoading(false);
    }

    fetchCreditNotes();
    return () => {
      cancelled = true;
    };
  }, [organisationId, pagination.cursor, pagination.limit]);

  if (error) {
    return <p className="text-destructive">{error}</p>;
  }

  return (
    <div>
      <div className="mb-4 flex items-center gap-3">
        {loading && <Spinner className="size-5" />}
      </div>

      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Reference</TableHead>
              <TableHead>Invoice</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Reason</TableHead>
              <TableHead className="text-right">Amount</TableHead>
              <TableHead>Created</TableHead>
              <TableHead>Issued</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {creditNotes.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-muted-foreground h-24 text-center">
                  No credit notes.
                </TableCell>
              </TableRow>
            ) : (
              creditNotes.map((cn) => (
                <TableRow key={cn.id}>
                  <TableCell>
                    <span className="font-mono text-xs">{cn.documentNumber}</span>
                  </TableCell>
                  <TableCell>
                    {cn.invoiceId ? (
                      <Link
                        to={`/invoices/${cn.invoiceId}${
                          cn.organisationId ? `?organisationId=${cn.organisationId}` : ""
                        }`}
                      >
                        <Badge variant="secondary" className="font-mono text-xs hover:bg-primary/20">
                          {cn.invoiceId.slice(0, 8)}...
                        </Badge>
                      </Link>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={statusVariant(cn.status)}>{cn.status}</Badge>
                  </TableCell>
                  <TableCell className="max-w-xs truncate">
                    {cn.reason ?? <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="text-right">
                    {formatCurrency(cn.amount, cn.currency)}
                  </TableCell>
                  <TableCell>{formatDateTime(cn.createdAt)}</TableCell>
                  <TableCell>
                    {cn.issuedAt ? (
                      formatDateTime(cn.issuedAt)
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <PaginationBar
        hasPrevious={pagination.hasPrevious}
        hasNext={pagination.hasMore}
        isLoading={loading}
        limit={pagination.limit}
        onPrevious={pagination.goPrevious}
        onNext={pagination.goNext}
        onLimitChange={pagination.setLimit}
      />
    </div>
  );
}
